(function (global) {
  function createBundleState({
    minCount = 3,
    maxCount = 150
  } = {}) {
    return {
      minCount: Number(minCount) || 3,
      maxCount: Number(maxCount) || 150,
      items: new Map(),
    };
  }

  function totalQuantity(state) {
    let total = 0;

    for (const item of state.items.values()) {
      total += item.quantity;
    }

    return total;
  }

  function totalPriceCents(state) {
    let total = 0;

    for (const item of state.items.values()) {
      total += item.priceCents * item.quantity;
    }

    return total;
  }

  function canAddMore(state) {
    return (
      !state.maxCount ||
      totalQuantity(state) < state.maxCount
    );
  }

  function addItem(state, product) {
    if (!canAddMore(state)) {
      return false;
    }

    const key = Number(product.variantId);

    const current = state.items.get(key);

    if (current) {
      current.quantity += 1;
    } else {
      state.items.set(key, {
        ...product,
        variantId: key,
        quantity: 1,
      });
    }

    return true;
  }

  function removeItem(state, variantId) {
    const key = Number(variantId);

    const current = state.items.get(key);

    if (!current) {
      return false;
    }

    if (current.quantity > 1) {
      current.quantity -= 1;
    } else {
      state.items.delete(key);
    }

    return true;
  }

  function meetsMinimum(state) {
    return totalQuantity(state) >= state.minCount;
  }

  function mainPageLayout(state) {
    const cards =
      Array.from(state.items.values())
        .map((item) => ({
          ...item
        }));

    const count = totalQuantity(state);

    let emptySlots;

    if (count < state.minCount) {
      emptySlots = state.minCount - count;
    } else {
      emptySlots = 1;
    }

    if (
      state.maxCount &&
      count >= state.maxCount
    ) {
      emptySlots = 0;
    }

    return {
      cards,
      emptySlots,
      nextPosition: count + 1,
    };
  }

  function findBundleVariant(
    variants,
    size
  ) {
    const wanted = Number(size);

    if (
      !Number.isFinite(wanted) ||
      wanted < 1
    ) {
      return null;
    }

    return (
      variants.find(
        (variant) =>
          Number(variant.size) === wanted &&
          variant.available !== false
      ) || null
    );
  }

  function bundlePriceMatches(
    state,
    parentVariant
  ) {
    if (!parentVariant) {
      return false;
    }

    return (
      Number(parentVariant.priceCents) ===
      totalPriceCents(state)
    );
  }

  function buildParentCartItem(
    state,
    parentVariant,
    {
      bundleTitle = '',
      freeGifts = []
    } = {}
  ) {
    const properties = {
      _Bundle: bundleTitle,
      _BundleSize:
        String(totalQuantity(state)),
      _BundleUniqueItems:
        String(state.items.size),
    };

    Array
      .from(state.items.values())
      .forEach(
        (item, index) => {
          properties[
            `Item ${index + 1}`
          ] =
            `${item.quantity} × ${item.title}`;
        }
      );

    freeGifts.forEach(
      (gift, giftIndex) => {
        properties[
          `Free Gift ${giftIndex + 1}`
        ] = gift;
      }
    );

    return {
      id: Number(parentVariant.id),
      quantity: 1,
      properties,
    };
  }

  const api = {
    createBundleState,
    addItem,
    removeItem,
    totalQuantity,
    totalPriceCents,
    meetsMinimum,
    canAddMore,
    mainPageLayout,
    findBundleVariant,
    bundlePriceMatches,
    buildParentCartItem,
  };

  if (
    typeof module !== 'undefined' &&
    module.exports
  ) {
    module.exports = api;
  }

  if (global) {
    global.GiftyBundleBuilder = api;
  }

  if (
    typeof document === 'undefined'
  ) {
    return;
  }

  function moneyFormatter(root) {
    const currency =
      root.dataset.currency || 'USD';

    const locale =
      root.dataset.locale ||
      document.documentElement.lang ||
      'en-US';

    try {
      const formatter =
        new Intl.NumberFormat(
          locale,
          {
            style: 'currency',
            currency,
          }
        );

      return (cents) =>
        formatter.format(
          (Number(cents) || 0) / 100
        );

    } catch (_) {

      return (cents) =>
        `$${(
          (Number(cents) || 0) / 100
        ).toFixed(2)}`;
    }
  }

  function getProductData(card) {
    return {
      variantId:
        Number(card.dataset.variantId),

      handle:
        card.dataset.handle || '',

      title:
        card.dataset.title || '',

      image:
        card.dataset.image || '',

      priceCents:
        Number(
          card.dataset.priceCents || 0
        ),
    };
  }

  function initBundle(root) {
    if (
      !root ||
      root.dataset.initialized === 'true'
    ) {
      return;
    }

    root.dataset.initialized = 'true';

    const state =
      createBundleState({
        minCount:
          Number(
            root.dataset.minCount || 3
          ),

        maxCount:
          Number(
            root.dataset.maxCount || 150
          ),
      });

    const formatMoney =
      moneyFormatter(root);

    const dialog =
      root.querySelector(
        '[data-bundle-dialog]'
      );

    const search =
      root.querySelector(
        '[data-bundle-search]'
      );

    const popupCount =
      root.querySelector(
        '[data-popup-count]'
      );

    const popupTotal =
      root.querySelector(
        '[data-popup-total]'
      );

    const addBundleButton =
      root.querySelector(
        '[data-add-bundle]'
      );

    const mainPreview =
      root.querySelector(
        '[data-main-preview]'
      );

    const toast =
      root.querySelector(
        '[data-bundle-toast]'
      );

    const bundleTitle =
      root.dataset.bundleTitle ||
      'Bundle';

    const addButtonLabel =
      root.dataset.addButtonLabel ||
      'ADD BUNDLE TO CART';

    const selectionLabel =
      root.dataset.selectionLabel ||
      'Product';

    const cartAddUrl =
      root.dataset.cartAddUrl ||
      '/cart/add.js';

    const cartUrl =
      root.dataset.cartUrl ||
      '/cart';

    const freeGifts =
      Array.from(
        root.querySelectorAll(
          '[data-free-gift-title]'
        )
      )
      .map(
        (node) =>
          node.dataset.freeGiftTitle
      )
      .filter(Boolean);

    const variantDataNode =
      root.querySelector(
        '[data-bundle-parent-variants]'
      );

    let parentVariants = [];

    try {
      parentVariants =
        variantDataNode
          ? JSON.parse(
              variantDataNode.textContent ||
              '[]'
            )
          : [];
    } catch (_) {
      parentVariants = [];
    }

    let toastTimer;

    function showToast(message) {
      if (!toast) {
        return;
      }

      toast.textContent = message;

      toast.hidden = false;

      clearTimeout(toastTimer);

      toastTimer =
        setTimeout(() => {
          toast.hidden = true;
        }, 3200);
    }

    function itemForVariant(
      variantId
    ) {
      return state.items.get(
        Number(variantId)
      );
    }

    function syncCards() {
      root
        .querySelectorAll(
          '[data-product-card]'
        )
        .forEach((card) => {

          const variantId =
            Number(
              card.dataset.variantId
            );

          const selected =
            itemForVariant(
              variantId
            );

          const qty =
            selected
              ? selected.quantity
              : 0;

          const add =
            card.querySelector(
              '[data-card-add]'
            );

          const controls =
            card.querySelector(
              '[data-card-controls]'
            );

          const qtyNode =
            card.querySelector(
              '[data-card-qty]'
            );

          const badge =
            card.querySelector(
              '[data-selected-badge]'
            );

          const plus =
            card.querySelector(
              '[data-increment]'
            );

          card.classList.toggle(
            'is-selected',
            qty > 0
          );

          if (qtyNode) {
            qtyNode.textContent =
              String(qty);
          }

          if (badge) {
            badge.hidden =
              qty === 0;
          }

          if (add) {
            add.hidden =
              qty > 0;
          }

          if (controls) {
            controls.hidden =
              qty === 0;
          }

          if (plus) {
            plus.disabled =
              !canAddMore(state);
          }
        });
    }

    function buildMainSelectedCard(
      item
    ) {
      const card =
        document.createElement(
          'article'
        );

      card.className =
        'bundle-builder__main-card';

      card.dataset.variantId =
        String(item.variantId);

      const remove =
        document.createElement(
          'button'
        );

      remove.type =
        'button';

      remove.className =
        'bundle-builder__main-remove';

      remove.dataset.previewRemove =
        '';

      remove.dataset.variantId =
        String(item.variantId);

      remove.setAttribute(
        'aria-label',
        `Remove one ${item.title}`
      );

      remove.textContent =
        '×';

      card.appendChild(
        remove
      );

      if (
        item.quantity > 1
      ) {
        const badge =
          document.createElement(
            'span'
          );

        badge.className =
          'bundle-builder__main-quantity';

        badge.textContent =
          `×${item.quantity}`;

        card.appendChild(
          badge
        );
      }

      const media =
        document.createElement(
          'div'
        );

      media.className =
        'bundle-builder__main-image-wrap';

      const image =
        document.createElement(
          'img'
        );

      image.className =
        'bundle-builder__main-image';

      image.src =
        item.image || '';

      image.alt =
        item.title || '';

      image.loading =
        'lazy';

      media.appendChild(
        image
      );

      card.appendChild(
        media
      );

      const title =
        document.createElement(
          'div'
        );

      title.className =
        'bundle-builder__main-title';

      title.textContent =
        item.title || '';

      card.appendChild(
        title
      );

      return card;
    }

    function buildMainAddCard(
      position
    ) {
      const button =
        document.createElement(
          'button'
        );

      button.type =
        'button';

      button.className =
        'bundle-builder__main-add';

      button.dataset.openBundle =
        '';

      button.setAttribute(
        'aria-label',
        'Choose products'
      );

      const plus =
        document.createElement(
          'span'
        );

      plus.className =
        'bundle-builder__main-plus';

      plus.textContent =
        '+';

      button.appendChild(
        plus
      );

      const label =
        document.createElement(
          'span'
        );

      label.className =
        'bundle-builder__main-add-label';

      label.textContent =
        `${selectionLabel} ${position}`;

      button.appendChild(
        label
      );

      return button;
    }

    function syncPreview() {
      if (!mainPreview) {
        return;
      }

      const layout =
        mainPageLayout(state);

      mainPreview.replaceChildren();

      layout.cards.forEach(
        (item) =>
          mainPreview.appendChild(
            buildMainSelectedCard(
              item
            )
          )
      );

      for (
        let i = 0;
        i < layout.emptySlots;
        i += 1
      ) {
        mainPreview.appendChild(
          buildMainAddCard(
            layout.nextPosition + i
          )
        );
      }
    }

    function syncSummary() {
      const count =
        totalQuantity(state);

      const total =
        totalPriceCents(state);

      if (popupCount) {
        popupCount.textContent =
          String(count);
      }

      if (popupTotal) {
        popupTotal.textContent =
          formatMoney(total);
      }

      if (
        addBundleButton
      ) {
        const parentVariant =
          findBundleVariant(
            parentVariants,
            count
          );

        const complete =
          meetsMinimum(state) &&
          Boolean(parentVariant) &&
          bundlePriceMatches(
            state,
            parentVariant
          );

        addBundleButton
          .classList
          .toggle(
            'is-incomplete',
            !complete
          );

        addBundleButton
          .setAttribute(
            'aria-disabled',
            complete
              ? 'false'
              : 'true'
          );

        if (
          addBundleButton
            .dataset
            .loading !== 'true'
        ) {
          addBundleButton.textContent =
            `${addButtonLabel} — ${formatMoney(total)}`;
        }
      }
    }

    function syncState() {
      syncCards();
      syncPreview();
      syncSummary();
    }

    function openDialog() {
      if (!dialog) {
        return;
      }

      if (dialog.showModal) {
        dialog.showModal();
      } else {
        dialog.setAttribute(
          'open',
          ''
        );
      }
    }

    function closeDialog() {
      if (!dialog) {
        return;
      }

      syncState();

      if (dialog.close) {
        dialog.close();
      } else {
        dialog.removeAttribute(
          'open'
        );
      }
    }

    async function addBundleToCart() {
      if (
        !meetsMinimum(state)
      ) {
        showToast(
          `Add at least ${state.minCount} products to continue.`
        );

        return;
      }

      const count =
        totalQuantity(state);

      if (
        count >
        state.maxCount
      ) {
        showToast(
          `You can add up to ${state.maxCount} products to this bundle.`
        );

        return;
      }

      const parentVariant =
        findBundleVariant(
          parentVariants,
          count
        );

      if (!parentVariant) {
        showToast(
          `${bundleTitle} is not configured for ${count} items. Add a Bundle Size ${count} variant.`
        );

        return;
      }

      if (
        !bundlePriceMatches(
          state,
          parentVariant
        )
      ) {
        showToast(
          `Bundle Size ${count} is priced incorrectly. Its Shopify variant price must equal ${formatMoney(totalPriceCents(state))}.`
        );

        return;
      }

      if (
        !addBundleButton ||
        addBundleButton
          .dataset
          .loading === 'true'
      ) {
        return;
      }

      addBundleButton
        .dataset
        .loading = 'true';

      addBundleButton.textContent =
        'ADDING BUNDLE...';

      const parentLine =
        buildParentCartItem(
          state,
          parentVariant,
          {
            bundleTitle,
            freeGifts
          }
        );

      try {
        const response =
          await fetch(
            cartAddUrl,
            {
              method:
                'POST',

              headers: {
                'Content-Type':
                  'application/json',

                'Accept':
                  'application/json'
              },

              body:
                JSON.stringify({
                  items: [
                    parentLine
                  ]
                }),
            }
          );

        if (
          !response.ok
        ) {
          let message =
            'Unable to add this bundle to cart.';

          try {
            const error =
              await response.json();

            message =
              error.description ||
              error.message ||
              message;

          } catch (_) {}

          throw new Error(
            message
          );
        }

        window.location.assign(
          cartUrl
        );

      } catch (error) {

        showToast(
          error.message ||
          'Unable to add this bundle to cart.'
        );

        addBundleButton
          .dataset
          .loading = 'false';

        syncSummary();
      }
    }

    root.addEventListener(
      'click',
      (event) => {

        const open =
          event.target.closest(
            '[data-open-bundle]'
          );

        if (open) {
          openDialog();
          return;
        }

        if (
          event.target.closest(
            '[data-close-bundle]'
          ) ||
          event.target.closest(
            '[data-done]'
          )
        ) {
          closeDialog();
          return;
        }

        const card =
          event.target.closest(
            '[data-product-card]'
          );

        const increment =
          event.target.closest(
            '[data-increment]'
          );

        const add =
          event.target.closest(
            '[data-card-add]'
          );

        const imageAdd =
          event.target.closest(
            '[data-image-add]'
          );

        if (
          card &&
          (
            increment ||
            add ||
            imageAdd
          )
        ) {

          if (
            !addItem(
              state,
              getProductData(card)
            )
          ) {
            showToast(
              `You can add up to ${state.maxCount} products to this bundle.`
            );
          }

          syncState();
          return;
        }

        const decrement =
          event.target.closest(
            '[data-decrement]'
          );

        if (
          card &&
          decrement
        ) {
          removeItem(
            state,
            card.dataset.variantId
          );

          syncState();
          return;
        }

        const previewRemove =
          event.target.closest(
            '[data-preview-remove]'
          );

        if (
          previewRemove &&
          previewRemove.dataset.variantId
        ) {
          removeItem(
            state,
            previewRemove.dataset.variantId
          );

          syncState();
          return;
        }

        if (
          event.target.closest(
            '[data-add-bundle]'
          )
        ) {
          addBundleToCart();
          return;
        }

        const loadMore =
          event.target.closest(
            '[data-load-more]'
          );

        if (loadMore) {
          loadMoreProducts(
            loadMore
          );
        }
      }
    );

    dialog?.addEventListener(
      'click',
      (event) => {
        if (
          event.target === dialog
        ) {
          closeDialog();
        }
      }
    );

    search?.addEventListener(
      'input',
      () => {

        const term =
          search.value
            .trim()
            .toLowerCase();

        root
          .querySelectorAll(
            '[data-product-card]'
          )
          .forEach(
            (card) => {

              card.hidden =
                Boolean(term) &&
                !(
                  card.dataset.search ||
                  ''
                ).includes(term);
            }
          );
      }
    );

    async function loadMoreProducts(
      button
    ) {
      if (
        button.dataset.loading ===
        'true'
      ) {
        return;
      }

      const cards =
        root.querySelector(
          '[data-bundle-cards]'
        );

      const totalPages =
        Number(
          cards?.dataset.totalPages ||
          1
        );

      const nextPage =
        Number(
          button.dataset.nextPage ||
          2
        );

      if (
        !cards ||
        nextPage > totalPages
      ) {
        button.hidden = true;
        return;
      }

      button.dataset.loading =
        'true';

      const oldLabel =
        button.textContent;

      button.textContent =
        'Loading...';

      try {
        const url =
          new URL(
            window.location.href
          );

        url.searchParams.set(
          'section_id',
          root.dataset.sectionId
        );

        url.searchParams.set(
          'page',
          String(nextPage)
        );

        const response =
          await fetch(
            url.toString(),
            {
              headers: {
                'X-Requested-With':
                  'XMLHttpRequest'
              }
            }
          );

        if (!response.ok) {
          throw new Error(
            'Could not load more products.'
          );
        }

        const html =
          await response.text();

        const doc =
          new DOMParser()
            .parseFromString(
              html,
              'text/html'
            );

        const incoming =
          doc.querySelector(
            `[data-gifty-bundle][data-block-id="${root.dataset.blockId}"] [data-bundle-cards]`
          );

        if (incoming) {
          Array
            .from(
              incoming.children
            )
            .forEach(
              (child) =>
                cards.appendChild(
                  child
                )
            );
        }

        button.dataset.nextPage =
          String(
            nextPage + 1
          );

        if (
          nextPage + 1 >
          totalPages
        ) {
          button.hidden = true;
        }

        syncState();

      } catch (error) {

        showToast(
          error.message ||
          'Could not load more products.'
        );

      } finally {

        button.dataset.loading =
          'false';

        if (!button.hidden) {
          button.textContent =
            oldLabel;
        }
      }
    }

    syncState();
  }

  function initAll() {
    document
      .querySelectorAll(
        '[data-gifty-bundle]'
      )
      .forEach(
        initBundle
      );
  }

  if (
    document.readyState ===
    'loading'
  ) {
    document.addEventListener(
      'DOMContentLoaded',
      initAll
    );
  } else {
    initAll();
  }

  document.addEventListener(
    'shopify:section:load',
    initAll
  );

})(
  typeof window !== 'undefined'
    ? window
    : globalThis
);